/**
 * PriceHistoryChart.jsx
 *
 * Simple SVG line chart showing 30-day fuel price history.
 * Shows price trend with today's price marked.
 */

import React, { useMemo } from 'react';

/**
 * PriceHistoryChart Component
 *
 * @param {Array} priceHistory - Array of { date, price } objects
 * @param {number} currentPrice - Today's spot price
 * @param {number} width - Chart width (default 400)
 * @param {number} height - Chart height (default 150)
 */
export function PriceHistoryChart({
  priceHistory = [],
  currentPrice = 3.00,
  width = 400,
  height = 150,
}) {
  // Calculate chart data
  const chartData = useMemo(() => {
    // Use price history or generate placeholder if empty
    let prices = priceHistory.map(p => p.price || p);

    // If no history, show flat line at current price
    if (prices.length === 0) {
      prices = [currentPrice];
    }

    // Ensure we have at least 2 points for a line
    if (prices.length === 1) {
      prices = [prices[0], prices[0]];
    }

    // Calculate min/max for scaling (with padding)
    const minPrice = Math.min(...prices) * 0.95;
    const maxPrice = Math.max(...prices) * 1.05;
    const priceRange = maxPrice - minPrice || 0.5; // Avoid division by zero

    // Chart dimensions (with padding)
    const padding = { top: 20, right: 40, bottom: 30, left: 50 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    // Generate points for the line
    const points = prices.map((price, i) => {
      const x = padding.left + (i / (prices.length - 1)) * chartWidth;
      const y = padding.top + chartHeight - ((price - minPrice) / priceRange) * chartHeight;
      return { x, y, price };
    });

    // Generate SVG path
    const pathD = points.reduce((path, point, i) => {
      return path + (i === 0 ? `M ${point.x},${point.y}` : ` L ${point.x},${point.y}`);
    }, '');

    // Generate area fill path (for gradient under line)
    const areaD = pathD +
      ` L ${points[points.length - 1].x},${padding.top + chartHeight}` +
      ` L ${padding.left},${padding.top + chartHeight} Z`;

    // Y-axis labels (4 price points)
    const yLabels = [];
    for (let i = 0; i <= 3; i++) {
      const price = minPrice + (priceRange * i / 3);
      const y = padding.top + chartHeight - (i / 3) * chartHeight;
      yLabels.push({ price, y });
    }

    // X-axis labels
    const xLabels = [];
    if (prices.length >= 2) {
      xLabels.push({ label: `-${prices.length - 1}d`, x: padding.left });
      if (prices.length > 15) {
        xLabels.push({ label: `-${Math.floor(prices.length / 2)}d`, x: padding.left + chartWidth / 2 });
      }
      xLabels.push({ label: 'today', x: padding.left + chartWidth });
    }

    return {
      points,
      pathD,
      areaD,
      yLabels,
      xLabels,
      padding,
      chartWidth,
      chartHeight,
      lastPoint: points[points.length - 1],
    };
  }, [priceHistory, currentPrice, width, height]);

  // Determine trend color
  const trendColor = useMemo(() => {
    if (priceHistory.length < 2) return '#60ffd0'; // Default teal
    const firstPrice = priceHistory[0]?.price || priceHistory[0];
    const lastPrice = priceHistory[priceHistory.length - 1]?.price || priceHistory[priceHistory.length - 1];
    if (lastPrice > firstPrice * 1.05) return '#ef4444'; // Red - prices up
    if (lastPrice < firstPrice * 0.95) return '#22c55e'; // Green - prices down
    return '#60ffd0'; // Teal - stable
  }, [priceHistory]);

  return (
    <div style={{
      backgroundColor: '#0c0c0c',
      borderRadius: '8px',
      padding: '12px',
      border: '1px solid #1a1a1a',
    }}>
      <div style={{
        fontSize: '12px',
        fontWeight: '600',
        color: '#f97316',
        marginBottom: '8px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <span>PRICE HISTORY ({priceHistory.length || 0} days)</span>
        <span style={{ color: '#525252', fontSize: '10px' }}>
          Range: ${chartData.yLabels[0]?.price.toFixed(2)} - ${chartData.yLabels[3]?.price.toFixed(2)}
        </span>
      </div>

      <svg width={width} height={height} style={{ display: 'block' }}>
        {/* Gradient definition */}
        <defs>
          <linearGradient id="priceGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={trendColor} stopOpacity="0.3" />
            <stop offset="100%" stopColor={trendColor} stopOpacity="0.05" />
          </linearGradient>
        </defs>

        {/* Grid lines */}
        {chartData.yLabels.map((label, i) => (
          <line
            key={i}
            x1={chartData.padding.left}
            y1={label.y}
            x2={chartData.padding.left + chartData.chartWidth}
            y2={label.y}
            stroke="#1a1a1a"
            strokeWidth="1"
          />
        ))}

        {/* Area fill */}
        <path
          d={chartData.areaD}
          fill="url(#priceGradient)"
        />

        {/* Price line */}
        <path
          d={chartData.pathD}
          fill="none"
          stroke={trendColor}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Today's price dot */}
        {chartData.lastPoint && (
          <>
            <circle
              cx={chartData.lastPoint.x}
              cy={chartData.lastPoint.y}
              r="5"
              fill={trendColor}
              stroke="#0a0a0a"
              strokeWidth="2"
            />
            {/* Price label next to dot */}
            <text
              x={chartData.lastPoint.x + 8}
              y={chartData.lastPoint.y + 4}
              fill={trendColor}
              fontSize="11"
              fontWeight="bold"
              fontFamily="monospace"
            >
              ${chartData.lastPoint.price.toFixed(2)}
            </text>
          </>
        )}

        {/* Y-axis labels */}
        {chartData.yLabels.map((label, i) => (
          <text
            key={i}
            x={chartData.padding.left - 8}
            y={label.y + 4}
            fill="#525252"
            fontSize="10"
            textAnchor="end"
            fontFamily="monospace"
          >
            ${label.price.toFixed(2)}
          </text>
        ))}

        {/* X-axis labels */}
        {chartData.xLabels.map((label, i) => (
          <text
            key={i}
            x={label.x}
            y={height - 8}
            fill="#525252"
            fontSize="10"
            textAnchor={i === 0 ? 'start' : i === chartData.xLabels.length - 1 ? 'end' : 'middle'}
            fontFamily="monospace"
          >
            {label.label}
          </text>
        ))}

        {/* No data message */}
        {priceHistory.length === 0 && (
          <text
            x={width / 2}
            y={height / 2}
            fill="#525252"
            fontSize="12"
            textAnchor="middle"
          >
            Price history will appear as days pass
          </text>
        )}
      </svg>
    </div>
  );
}

export default PriceHistoryChart;
